import React, { useState, useRef, useEffect } from 'react';
import { Table, Spin, Select, Tooltip, Button, message } from 'antd';
import { getData } from '../../config/api-connect';
import { DownloadOutlined, LoadingOutlined,CheckCircleTwoTone,CloseCircleTwoTone } from '@ant-design/icons';
import JsBarcode from 'jsbarcode';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as Styled from './style.js';
import sporthoodLogo from '../../assets/images/sp-logo-white.png';
import {getCDNUrl} from '../../utils/imageUtil.js'
import {getSearchFilter,getDropdownFilter} from '../../utils/filterHelper.jsx'
import Spinner from '../../components/spinner'

const { Option } = Select;

const DEFAULT_AVATAR = 'https://www.sporthood.in/assets/img/avatar.png';

const IDCards = () => {
  let local_city = JSON.parse(localStorage.getItem('cityId'));
  let local_selectedVenueId = JSON.parse(localStorage.getItem('selectedVenueId'));
  if (!local_selectedVenueId) local_selectedVenueId = 'All';

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [venues, setVenues] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState(local_city);
  const [venuelist, setVenuelist] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState(local_selectedVenueId);
  const [studentInfoData, setStudentInfoData] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  
  const cardRef = useRef(null);
  const barcodeRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('selectedVenueId', JSON.stringify(selectedVenueId));
    localStorage.setItem('cityId', JSON.stringify(cityId));
    if (cityId !== '') getVisitorPass();
  }, [selectedVenueId, cityId]);

  useEffect(() => {
    getVenues();
  }, []);

  useEffect(() => {
    if (cities.length) getVenuesInCity();
  }, [cities, cityId]);

  const handleCityChange = (value) => {
    if (value.length > 0) {
      const last_selected = value[value.length - 1];
      if (last_selected === 'All') setCityId(['All']);
      else setCityId(value.filter((v) => v !== 'All'));
    } else setCityId(['All']);
  };

  const getVenuesInCity = () => {
    if (cities[0] && !cityId) setCityId([cities[0].id]);

    let filteredVenues;
    if (cityId === 'All' || (Array.isArray(cityId) && cityId.includes('All'))) {
      filteredVenues = venues;
    } else {
      filteredVenues = Array.isArray(cityId)
        ? venues.filter((item) => cityId.includes(item.city))
        : venues;
    }

    setVenuelist(filteredVenues);
    const venueExists = filteredVenues.some((item) => item.id === selectedVenueId);
    if (!venueExists) setSelectedVenueId('All');
  };

  const getVenues = async () => {
    try {
      const response = await getData('/business/get_venues_for_buddies/');
      setVenues(response.result);
      setCities(response.cities);
    } catch (error) {
      console.log(error.message);
    }
  };

  const getVisitorPass = async () => {
    setLoading(true);
    try {
      const response = await getData(
        `/business/get_visitor_pass/?venue_id=${selectedVenueId}&city_id=${cityId}`
      );
      setStudentInfoData(response.results || []);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderHeader = (title) => (
    <div style={{ textAlign: 'center', fontWeight: 'bold', textWrap: 'nowrap' }}>{title}</div>
  );

  const generateCardImage = async (customer) => {
    setSelectedCustomer(customer);
    await new Promise(resolve => requestAnimationFrame(resolve));

    try {
      JsBarcode(barcodeRef.current, customer.id, {
        format: 'CODE128',
        width: 2,
        height: 90,
        displayValue: false,
        margin: 0,
        background: 'transparent',
        lineColor: '#1c4951',
      });

      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        backgroundColor: null,
        allowTaint: false,
        useCORS: true,
      });

      return canvas.toDataURL('image/png');
    } catch (err) {
      console.error('Card generation failed', err);
      throw err;
    }
  };

  const handleDownload = async (customer) => {
    setDownloading(true);
    setDownloadingId(customer.id);

    try {
      const imageData = await generateCardImage(customer);
      
      const link = document.createElement('a');
      const customerName = customer.name || 'Unknown';
      const batchName = customer.batch || 'NoBatch';
      link.download = `${customerName.replace(/\s+/g, '_')}_${batchName.replace(/\s+/g, '_')}_ID_Card.png`;
      link.href = imageData;
      link.click();
    } catch (err) {
      console.error('Download failed', err);
      message.error('Failed to download ID card');
    } finally {
      setDownloading(false);
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    const dataToDownload = filteredData.length > 0 ? filteredData : studentInfoData;
    if (dataToDownload.length === 0) {
      message.warning('No records to download');
      return;
    }

    setDownloadingAll(true);
    setDownloadProgress(0);
    const zip = new JSZip();

    try {
      message.info(`Generating ${dataToDownload.length} ID cards...`);

      for (let i = 0; i < dataToDownload.length; i++) {
        const customer = dataToDownload[i];
        
        try {
          const imageData = await generateCardImage(customer);
          const base64Data = imageData.split(',')[1];
          
          const customerName = customer.name || 'Unknown';
          const batchName = customer.batch || 'NoBatch';
          const fileName = `${customerName.replace(/\s+/g, '_')}_${batchName.replace(/\s+/g, '_')}_${i + 1}_ID_Card.png`;
          
          zip.file(fileName, base64Data, { base64: true });
          
          setDownloadProgress(Math.round(((i + 1) / dataToDownload.length) * 100));
        } catch (err) {
          console.error(`Failed to generate card for ${customer.name}`, err);
        }

        // Small delay to prevent browser freezing
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      message.success('Packaging cards into ZIP file...');
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `Visitor_ID_Cards_${new Date().toISOString().split('T')[0]}.zip`);
      
      message.success(`Successfully downloaded ${dataToDownload.length} ID cards`);
    } catch (err) {
      console.error('Download all failed', err);
      message.error('Failed to download all cards');
    } finally {
      setDownloadingAll(false);
      setDownloadProgress(0);
    }
  };

  const columns = [
    {
      title: renderHeader('Name'),
      dataIndex: 'name',
      align: 'center',
      ...getSearchFilter('name', 'Name')
    },
    {
      title: renderHeader('Mobile'),
      dataIndex: 'contact_number',
      align: 'center',
      ...getSearchFilter('contact_number', 'Mobile')
    },
    {
      title: renderHeader('Venue'),
      dataIndex: 'venue',
      align: 'center',
      ...getDropdownFilter('venue',studentInfoData)
    },
    {
      title: renderHeader('Batch'),
      dataIndex: 'batch',
      align: 'center',
      ...getDropdownFilter('batch',studentInfoData)
    },
    {
      title: renderHeader('Type'),
      dataIndex: 'type',
      align: 'center',
      ...getDropdownFilter('type',studentInfoData)
    },
    {
      title: renderHeader('Photo Uploaded'),
      dataIndex: 'profile_pic',
      align: 'center',
      filters: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' }
      ],
      onFilter: (value, record) => {
        const hasPhoto =
          record.profile_pic &&
          record.profile_pic !== DEFAULT_AVATAR;

        return value === 'yes' ? hasPhoto : !hasPhoto;
      },
      render: (profilePic) => {
        const hasPhoto = profilePic && profilePic !== DEFAULT_AVATAR;
        return hasPhoto ? (
          <CheckCircleTwoTone twoToneColor="#52c41a" />
        ) : (
          <CloseCircleTwoTone twoToneColor="#ff4d4f" />
        );
      }
    },
    {
      title: renderHeader('Action'),
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Download ID Card">
          <Styled.DownloadButton 
            onClick={() => handleDownload(record)}
            disabled={downloading || downloadingAll}
            style={{
              opacity: (downloading && downloadingId !== record.id) || downloadingAll ? 0.5 : 1,
              cursor: downloading || downloadingAll ? 'not-allowed' : 'pointer'
            }}
          >
            {downloadingId === record.id ? <LoadingOutlined /> : <DownloadOutlined />}
          </Styled.DownloadButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Spin spinning={loading} indicator={<Spinner />}>
      <Styled.MainContainer>
        <Styled.TopDiv>
          <Styled.SelectorContainer>
            {cities.length > 1 && (
              <Styled.Selector>
                <span className="indicatorText">City</span>
                <Select
                  mode="multiple"
                  className="mainSelector"
                  style={{ width: 150 }}
                  showSearch
                  placeholder="Search to Select"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.name.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleCityChange}
                  value={cityId}
                  bordered={false}
                >
                  <Option value="All" name="All">
                    All
                  </Option>
                  {cities.map((item) => (
                    <Option value={item.id} key={item.id} name={item.name}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Styled.Selector>
            )}

            {venuelist && venuelist.length > 0 && (
              <Styled.Selector>
                <span className="indicatorText">Venue</span>
                <Select
                  className="mainSelector"
                  style={{ width: 300 }}
                  showSearch
                  placeholder="Search to Select"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.name.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={setSelectedVenueId}
                  value={selectedVenueId}
                  bordered={false}
                >
                  <Option value="All" name="All">
                    All
                  </Option>
                  {venuelist.map((item) => (
                    <Option value={item.id} key={item.id} name={item.name}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Styled.Selector>
            )}
          </Styled.SelectorContainer>

          <Styled.DownloadAll
            type="primary"
            icon={downloadingAll ? <LoadingOutlined /> : <DownloadOutlined />}
            onClick={handleDownloadAll}
            disabled={downloadingAll || studentInfoData.length === 0}
          >
            {downloadingAll ? `Downloading ${downloadProgress}%` : 
            `Download All Cards (${filteredData.length > 0 ? filteredData.length : studentInfoData.length})`}
          </Styled.DownloadAll>
        </Styled.TopDiv>

        <Styled.Header>ID Cards</Styled.Header>

        <div className="recordCount">{filteredData.length > 0 ? filteredData.length : studentInfoData.length} records</div>
        <Table
          className="drill-down-table table_container"
          rowKey="id"
          columns={columns}
          dataSource={studentInfoData}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
          onChange={(pagination, filters, sorter, extra) => {
            setFilteredData(extra.currentDataSource);
          }}
        />

        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          {selectedCustomer && (
            <Styled.DownloadableCard ref={cardRef}>
              <Styled.LogoContainer>
                <Styled.Logo src={sporthoodLogo} crossOrigin="anonymous" />
              </Styled.LogoContainer>

              <Styled.LogoCover />

              <Styled.CardContent>
                <Styled.PhotoContainer>
                  <Styled.Photo 
                    src={getCDNUrl(selectedCustomer.profile_pic)} 
                    crossOrigin="anonymous" 
                  />
                </Styled.PhotoContainer>

                <Styled.InfoSection>
                  <Styled.Title>{selectedCustomer.type ? selectedCustomer.type.toUpperCase() : '' } ID CARD</Styled.Title>

                  <Styled.InfoRow>
                    <Styled.Label>Name</Styled.Label>
                    <Styled.Value>: {selectedCustomer.name}</Styled.Value>
                  </Styled.InfoRow>
                  {selectedCustomer.type && selectedCustomer.type !== 'Staff' &&
                    <Styled.InfoRow>
                      <Styled.Label>Batch</Styled.Label>
                      <Styled.Value>: {selectedCustomer.batch}</Styled.Value>
                    </Styled.InfoRow>
                  }
                  <Styled.InfoRow>
                    <Styled.Label>Contact Number</Styled.Label>
                    <Styled.Value>: {selectedCustomer.contact_number}</Styled.Value>
                  </Styled.InfoRow>

                  <Styled.BarcodeContainer>
                    <svg ref={barcodeRef}></svg>
                  </Styled.BarcodeContainer>
                </Styled.InfoSection>
              </Styled.CardContent>
            </Styled.DownloadableCard>
          )}
        </div>
      </Styled.MainContainer>
    </Spin>
  );
};

export default IDCards;